import { type ComponentPropsWithoutRef, memo, useMemo } from 'react'
import Markdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { plainTextMath } from '../../lib/agent/plainTextMath'
import { completePartialMarkdown } from '../../utils/completePartialMarkdown'

interface ChatMarkdownProps {
  content: string
  /** Set while the text is still streaming in — half-typed markup is patched up
   *  so the reply doesn't flicker through its own syntax. */
  streaming?: boolean
}

const REMARK_PLUGINS = [remarkGfm]

// Open links (e.g. document download URLs) in a new tab so they don't navigate
// away from the chat.
const COMPONENTS: Components = {
  a: ({ node: _node, ...props }: ComponentPropsWithoutRef<'a'> & { node?: unknown }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
}

export const ChatMarkdown = memo(function ChatMarkdown({ content, streaming = false }: ChatMarkdownProps) {
  const source = useMemo(() => {
    const plain = plainTextMath(content)
    return streaming ? completePartialMarkdown(plain) : plain
  }, [content, streaming])
  return (
    <div className="prose prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 max-w-none">
      <Markdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
        {source}
      </Markdown>
    </div>
  )
})
